import { useState } from "react";
import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import CheckBox from "@components/ui/selection/CheckBox";
import type { Contact } from "types/contact";

const CIVILITE_OPTIONS = [
  { value: "M.", label: "M." },
  { value: "Mme", label: "Mme" },
  { value: "Société", label: "Société" },
];

const PAYS_OPTIONS = [
  { value: "FRANCE", label: "FRANCE" },
  { value: "BELGIQUE", label: "BELGIQUE" },
  { value: "SUISSE", label: "SUISSE" },
  { value: "LUXEMBOURG", label: "LUXEMBOURG" },
];

export interface ContactFormModalProps {
  contact: Contact | null;
  onClose: () => void;
  onSave: (data: Partial<Contact>) => void;
  saving?: boolean;
}

export default function ContactFormModal({ contact, onClose, onSave, saving }: ContactFormModalProps) {
  const [civilite, setCivilite] = useState(contact?.civilite || "");
  const [nom, setNom] = useState(contact?.nom || "");
  const [prenom, setPrenom] = useState(contact?.prenom || "");
  const [role, setRole] = useState(contact?.role || "");
  const [phone, setPhone] = useState(contact?.phone || "");
  const [email, setEmail] = useState(contact?.email || "");
  const [adresse, setAdresse] = useState(contact?.adresse || "");
  const [pays, setPays] = useState(contact?.pays || "FRANCE");
  const [isPrimary, setIsPrimary] = useState(contact?.is_primary || false);

  const isEdit = !!contact;

  const handleSubmit = () => {
    onSave({
      civilite: civilite as Contact["civilite"],
      nom,
      prenom,
      role,
      phone,
      email,
      adresse,
      pays,
      is_primary: isPrimary,
    });
  };

  return (
    <BaseModal
      title={isEdit ? "Modifier le contact" : "Ajouter un contact"}
      startIcon={<Icon1 name="user" size={20} />}
      onClose={onClose}
      width="max-w-xl"
    >
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField label="Nom" value={nom} onChange={setNom} />
          </div>
          <div className="flex-1">
            <DropDown label="Civilité" options={CIVILITE_OPTIONS} value={civilite} onSelect={(v) => setCivilite(String(v))} />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField label="Prénom" value={prenom} onChange={setPrenom} />
          </div>
          <div className="flex-1">
            <TextField label="Rôle" value={role} onChange={setRole} />
          </div>
        </div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField label="Téléphone" type="phone" value={phone} onChange={setPhone} />
          </div>
          <div className="flex-1">
            <DropDown label="Pays" options={PAYS_OPTIONS} value={pays} onSelect={(v) => setPays(String(v))} />
          </div>
        </div>
        <TextField label="Email" value={email} onChange={setEmail} />
        <TextField label="Adresse (facturation)" value={adresse} onChange={setAdresse} />
        <div className="flex flex-row items-center gap-2 cursor-pointer" onClick={() => setIsPrimary(!isPrimary)}>
          <CheckBox checked={isPrimary} onChange={() => setIsPrimary(!isPrimary)} />
          <span className="text-h3 text-neutral-700">Contact principal</span>
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={saving ? "Enregistrement..." : (isEdit ? "Enregistrer" : "Ajouter")}
          onClick={handleSubmit}
          disabled={!nom.trim() || saving}
        />
      </div>
    </BaseModal>
  );
}
